'use client';

import type { SearchItem } from '@repo/api';

const RECENT_SEARCHES_KEY = 'recent_building_searches';
const MAX_RECENT_SEARCHES = 6;

export type RecentBuildingSearch = SearchItem & {
  searchedAt: string; // ISO timestamp when the search was performed
};

/**
 * Get recent building searches from localStorage
 */
export function getRecentSearches(): RecentBuildingSearch[] {
  if (typeof window === 'undefined') return [];

  try {
    const data = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!data) return [];
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error getting recent searches:', error);
    return [];
  }
}

/**
 * Save a building search to recent searches
 * Removes duplicates and adds to the beginning of the list
 */
export function saveRecentSearch(searchItem: SearchItem): void {
  if (typeof window === 'undefined') return;

  try {
    const recent = getRecentSearches();

    // Remove existing entry if it exists (by buildingId)
    const filtered = recent.filter(item => item.buildingId !== searchItem.buildingId);

    // Add new search at the beginning with timestamp
    const newSearch: RecentBuildingSearch = {
      ...searchItem,
      searchedAt: new Date().toISOString(),
    };

    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);

    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving recent search:', error);
  }
}

/**
 * Clear all recent searches
 */
export function clearRecentSearches(): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch (error) {
    console.error('Error clearing recent searches:', error);
  }
}

/**
 * Remove a specific search from recent searches
 */
export function removeRecentSearch(buildingId: string): void {
  if (typeof window === 'undefined') return;

  try {
    const recent = getRecentSearches();
    const filtered = recent.filter(item => item.buildingId !== buildingId);
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing recent search:', error);
  }
}
